/**
 * NutritionCoachPanel.jsx — iter825 (Feb 2026)
 *
 * "🧠 AI Nutrition Coach" — weekly Bulgarian narrative generated by
 * Claude Sonnet 4.5. Shows trend analysis over 4 weeks, top gaps, and
 * 3 concrete micro-corrections. Each correction is linked to real
 * recipes from public_recipes (never hallucinated).
 *
 * Cached in localStorage (1 fresh generation per 24h to avoid burning
 * LLM budget on repeated dashboard opens).
 */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import {
  Brain, Loader2, TrendingUp, TrendingDown, Minus, Utensils,
  Sparkles, Target, RefreshCw, ChefHat, ChevronRight,
} from "lucide-react";
import { useAuth } from "@/App";
import { emitHealthEvent } from "@/utils/healthEvents";

const CACHE_KEY = "bhi_nutrition_coach_cache_v1";
const CACHE_TTL_MS = 24 * 60 * 60 * 1000; // 24h

const TREND_ICON = {
  improving:     { icon: TrendingDown, cls: "text-emerald-500", label: "низходящ" },
  worsening:     { icon: TrendingUp,   cls: "text-rose-500",    label: "възходящ" },
  stable:        { icon: Minus,        cls: "text-slate-500",   label: "стабилен" },
  insufficient:  { icon: Minus,        cls: "text-slate-400 opacity-60", label: "недостатъчно данни" },
};

const COMP_TREND_ICON = {
  improving:     { icon: TrendingUp,   cls: "text-emerald-500", label: "възходящ" },
  worsening:     { icon: TrendingDown, cls: "text-rose-500",    label: "низходящ" },
  stable:        { icon: Minus,        cls: "text-slate-500",   label: "стабилен" },
  insufficient:  { icon: Minus,        cls: "text-slate-400 opacity-60", label: "недостатъчно данни" },
};

export default function NutritionCoachPanel({ isDark = false }) {
  const { API, user } = useAuth();
  const [data, setData]       = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr]         = useState(null);
  const [fromCache, setFromCache] = useState(false);

  // Load from cache on mount
  useEffect(() => {
    if (!user) return;
    try {
      const raw = localStorage.getItem(CACHE_KEY);
      if (!raw) return;
      const cached = JSON.parse(raw);
      if (cached?.user_id !== user.id && cached?.user_id !== user._id) return;
      if (!cached?.timestamp || Date.now() - cached.timestamp > CACHE_TTL_MS) return;
      setData(cached.data);
      setFromCache(true);
    } catch { /* noop */ }
  }, [user]);

  const generate = useCallback(async (force = false) => {
    if (loading) return;
    setLoading(true); setErr(null); setFromCache(false);
    try {
      const { data } = await axios.post(`${API}/bhi/nutrition-coach/generate`, { weeks: 4 });
      setData(data);
      try {
        localStorage.setItem(CACHE_KEY, JSON.stringify({
          user_id: user?.id || user?._id,
          timestamp: Date.now(),
          data,
        }));
      } catch { /* localStorage may fail in private mode */ }
      // iter826 — Notify CoachJournalPanel to re-fetch its list + chart.
      // Only emit if a fresh (non-cached) generation actually persisted
      // a new journal entry (i.e. journal_id present).
      if (data?.journal_id && !data?.cached) {
        emitHealthEvent("coach-journal-changed");
      }
    } catch (e) {
      setErr(e?.response?.data?.detail || e.message || "Неуспешно генериране.");
    } finally {
      setLoading(false);
    }
  }, [API, loading, user]);

  const cardCls = `rounded-2xl border ${
    isDark ? "bg-[hsl(222,47%,11%)] border-[hsl(223,47%,18%)]" : "bg-white border-slate-200"
  }`;

  if (!user) return null;

  const snap   = data?.snapshot;
  const report = data?.report;

  return (
    <section
      className={`${cardCls} p-5 mb-4`}
      data-testid="nutrition-coach-panel"
    >
      <header className="flex items-start gap-3 mb-3">
        <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-indigo-500 to-fuchsia-500 flex items-center justify-center shrink-0">
          <Brain className="w-5 h-5 text-white" />
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="text-lg font-extrabold bg-gradient-to-r from-indigo-500 to-fuchsia-500 bg-clip-text text-transparent">
            🧠 AI Nutrition Coach
          </h3>
          <p className={`text-xs mt-1 ${isDark ? "text-gray-300" : "text-gray-600"}`}>
            Седмичен анализ на посоката в спазването и 3 конкретни микро-корекции с рецепти от базата. Powered by Claude Sonnet 4.5.
          </p>
        </div>
        <button
          type="button"
          onClick={() => generate(true)}
          disabled={loading}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[11px] font-bold bg-gradient-to-r from-indigo-500 to-fuchsia-500 text-white hover:opacity-90 disabled:opacity-40 transition"
          data-testid="nutrition-coach-generate-btn"
        >
          {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
          {data ? "Обнови" : "Генерирай"}
        </button>
      </header>

      {err && (
        <div className="p-3 rounded-lg bg-red-500/10 text-red-500 text-sm" data-testid="nutrition-coach-error">
          {err}
        </div>
      )}

      {!data && !loading && !err && (
        <div className={`p-4 rounded-lg text-sm ${isDark ? "bg-slate-800 text-slate-300" : "bg-slate-50 text-slate-700"}`} data-testid="nutrition-coach-empty">
          Кликни „Генерирай“, за да получиш персонализиран 4-седмичен AI анализ на Вашата хранителна тенденция.
        </div>
      )}

      {loading && (
        <div className="py-8 flex items-center justify-center opacity-70" data-testid="nutrition-coach-loading">
          <Loader2 className="w-6 h-6 animate-spin mr-2" />
          <span className="text-sm">Claude Sonnet 4.5 анализира посоката за последните 4 седмици… (~15-25s)</span>
        </div>
      )}

      {data && (
        <div className="space-y-4" data-testid="nutrition-coach-report">
          {fromCache && (
            <div className="text-[10px] italic opacity-50">
              Показва се последно генерираният доклад (кеш ≤ 24h). Натисни „Обнови“ за нов.
            </div>
          )}

          {/* Ready guardrail */}
          {snap && !snap.ready_for_ai && (
            <div className="p-3 rounded-lg bg-amber-500/10 text-amber-700 dark:text-amber-300 text-sm" data-testid="nutrition-coach-guardrail">
              {snap.guardrail_reason || "Недостатъчно данни."}
            </div>
          )}

          {report && (
            <>
              {/* Headline */}
              <div className={`p-3 rounded-lg ${isDark ? "bg-indigo-500/10" : "bg-indigo-50"}`}>
                <div className="text-sm font-bold leading-relaxed">
                  {report.headline}
                </div>
                <div className="text-[11px] mt-1 opacity-80">
                  {report.trend_analysis}
                </div>
                {report.continuity_note && (
                  <div
                    className={`mt-2 pt-2 border-t border-indigo-200 dark:border-indigo-700 text-[11px] flex items-start gap-1.5 ${isDark ? "text-indigo-200" : "text-indigo-800"}`}
                    data-testid="nutrition-coach-continuity"
                  >
                    <span className="mt-[1px]">🧠</span>
                    <em>{report.continuity_note}</em>
                  </div>
                )}
              </div>

              {/* Trend strip */}
              {snap && (
                <TrendStrip trend={snap.trend} weekly={snap.weekly} isDark={isDark} />
              )}

              {/* Top gaps */}
              {(report.top_gaps || []).length > 0 && (
                <div>
                  <div className="text-[10px] uppercase font-bold tracking-widest mb-1.5 opacity-60 flex items-center gap-1">
                    <Target className="w-3 h-3" /> Основни пропуски
                  </div>
                  <div className="grid md:grid-cols-2 gap-2" data-testid="nutrition-coach-gaps">
                    {report.top_gaps.map((g, i) => (
                      <div key={i} className={`p-2.5 rounded-lg text-[11px] ${isDark ? "bg-rose-500/10" : "bg-rose-50"}`}>
                        <div className="font-bold text-rose-700 dark:text-rose-300 mb-0.5">
                          {g.gap}
                        </div>
                        <div className="opacity-80">{g.why_it_matters}</div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Micro-corrections */}
              {(report.micro_corrections || []).length > 0 && (
                <div>
                  <div className="text-[10px] uppercase font-bold tracking-widest mb-1.5 opacity-60 flex items-center gap-1">
                    <Sparkles className="w-3 h-3" /> Микро-корекции за следващата седмица
                  </div>
                  <div className="space-y-2" data-testid="nutrition-coach-corrections">
                    {report.micro_corrections.map((c, i) => (
                      <CorrectionCard key={i} c={c} idx={i + 1} isDark={isDark} />
                    ))}
                  </div>
                </div>
              )}

              {/* Closing */}
              {report.closing && (
                <div className={`p-3 rounded-lg text-[12px] italic text-center ${isDark ? "bg-fuchsia-500/10 text-fuchsia-300" : "bg-fuchsia-50 text-fuchsia-700"}`}>
                  {report.closing}
                </div>
              )}

              <div className="flex items-center justify-between text-[10px] opacity-50 pt-1">
                <span>
                  {data.ai_generated ? `Модел: ${data.model}` : "Fallback (deterministic)"}
                  {data.latency_ms > 0 && ` · ${(data.latency_ms / 1000).toFixed(1)}s`}
                </span>
                <span>{data.disclaimer}</span>
              </div>
            </>
          )}
        </div>
      )}
    </section>
  );
}

// ─────────── Sub-components ───────────

function TrendStrip({ trend, weekly, isDark }) {
  const items = [
    { key: "nis", label: "NIS",        map: TREND_ICON },
    { key: "dil", label: "DIL",        map: TREND_ICON },
    { key: "uis", label: "UIS",        map: TREND_ICON },
    { key: "compliance", label: "Съответствие", map: COMP_TREND_ICON },
  ];
  return (
    <div className="grid grid-cols-2 md:grid-cols-4 gap-2" data-testid="nutrition-coach-trend-strip">
      {items.map((it) => {
        const t = it.map[trend[it.key]] || it.map.stable;
        const Icon = t.icon;
        return (
          <div key={it.key} className={`p-2.5 rounded-lg flex items-center gap-2 ${isDark ? "bg-slate-800/60" : "bg-slate-50"}`}>
            <Icon className={`w-4 h-4 ${t.cls}`} />
            <div>
              <div className="text-[10px] font-bold uppercase tracking-widest opacity-60">{it.label}</div>
              <div className={`text-[11px] font-semibold ${t.cls}`}>{t.label}</div>
            </div>
          </div>
        );
      })}
    </div>
  );
}

function CorrectionCard({ c, idx, isDark }) {
  const bg = isDark ? "bg-slate-800/60" : "bg-slate-50";
  return (
    <motion.div
      layout
      className={`rounded-lg border ${isDark ? "border-slate-700" : "border-slate-200"} ${bg} p-3`}
      data-testid={`nutrition-coach-correction-${idx}`}
    >
      <div className="flex items-start gap-2 mb-2">
        <div className="w-6 h-6 rounded-full bg-gradient-to-br from-indigo-500 to-fuchsia-500 text-white text-[11px] font-bold flex items-center justify-center shrink-0">
          {idx}
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-sm font-bold">{c.title}</div>
          <div className="text-[11px] mt-0.5 opacity-80">{c.action}</div>
        </div>
      </div>
      <div className="flex items-center gap-1.5 flex-wrap mb-2">
        {c.target_pathway && (
          <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[10px] bg-cyan-100 text-cyan-700 dark:bg-cyan-500/20 dark:text-cyan-300">
            🧬 {c.target_pathway}
          </span>
        )}
        {c.food_hint && (
          <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[10px] bg-emerald-100 text-emerald-700 dark:bg-emerald-500/20 dark:text-emerald-300">
            🥗 {c.food_hint}
          </span>
        )}
      </div>
      {c.expected_effect && (
        <div className="text-[10px] italic opacity-70 mb-2">
          Очакван ефект: {c.expected_effect}
        </div>
      )}

      {(c.recipes || []).length > 0 ? (
        <div>
          <div className="text-[9px] font-bold uppercase tracking-widest mb-1 opacity-60 flex items-center gap-1">
            <ChefHat className="w-3 h-3" /> Рецепти от базата
          </div>
          <div className="space-y-1" data-testid={`nutrition-coach-recipes-${idx}`}>
            {c.recipes.map((r) => (
              <div key={r.id} className="flex items-center gap-2 p-1.5 rounded bg-white dark:bg-slate-900/60">
                <ChevronRight className="w-3 h-3 opacity-40 shrink-0" />
                <span className="text-[11px] font-semibold flex-1 min-w-0 truncate">{r.name_bg}</span>
                <span className="text-[10px] opacity-60 shrink-0 flex items-center gap-1">
                  <Utensils className="w-2.5 h-2.5" /> {r.meal_type} · {r.calories}kcal
                </span>
              </div>
            ))}
          </div>
        </div>
      ) : (
        <div className="text-[10px] italic opacity-40">Няма рецепти в базата за тази храна.</div>
      )}
    </motion.div>
  );
}
